import * as Sentry from '@sentry/react';
import React from 'react';
import { Offcanvas } from '@edgebox/react-components';
import { Navigate, Route, Routes } from 'react-router';
import { BackendAddContract } from './Backend/Billing/BackendAddContract';
import { BackendAddContractRevision } from './Backend/Billing/BackendAddContractRevision';
import { BackendCustomerDetail } from './Backend/Billing/BackendCustomerDetail';
import { BackendCustomerList } from './Backend/Billing/BackendCustomerList';
import { BackendInvoiceList } from './Backend/Billing/BackendInvoiceList';
import { BackendSettings } from './Backend/settings/BackendSettings';
import { BackendLogs } from './Backend/Syndication/BackendLogs';
import { BackendProjectList } from './Backend/Syndication/BackendProjectList';
import { BackendSiteList } from './Backend/Syndication/BackendSiteList';
import { BackendSyncCoreList } from './Backend/Syndication/BackendSyncCoreList';
import { BackendFormulaPlayground } from './Backend/Syndication/BackendFormulaPlayground';
import { BackendAiManagement } from './Backend/Syndication/BackendAiManagement';
import { AccountCompany } from './Customer/Account/AccountCompany';
import { AccountBilling } from './Customer/Account/AccountBilling';
import { AccountPayment } from './Customer/Account/AccountPayment';
import { AccountProfile } from './Customer/Account/AccountProfile';
import { AccountUsers } from './Customer/Account/AccountUsers';
import { AccountTabsLayout } from './Customer/Account/AccountTabsLayout';
import { CheckoutPage } from './Customer/CheckoutPage';
import { ContentDashboard } from './Customer/Syndication/ContentDashboard';
import { ContractDetail } from './Customer/Syndication/ContractDetail';
import { ContractList } from './Customer/Syndication/ContractList';
import { ContractUpgrade } from './Customer/Syndication/ContractUpgrade';
import { ProjectDetail } from './Customer/Syndication/ProjectDetail';
import { ProjectAdd } from './Customer/Syndication/ProjectAdd';
import { ProjectList } from './Customer/Syndication/ProjectList';
import { RegisterMultipleSites } from './Customer/Syndication/RegisterMultipleSites';
import { RegisterSite } from './Customer/Syndication/RegisterSite';
import { SiteDetail } from './Customer/Syndication/SiteDetail';
import { SyncCoreDetail } from './Customer/Syndication/SyncCoreDetail';
import { SyncCoreList } from './Customer/Syndication/SyncCoreList';
import { NotFoundPage } from './NotFoundPage';
import { ILocationProp, WithParams, withLocation } from './RouterHelper';
import { SidebarNavigation } from './SidebarNavigation';
import { Dashboard } from './Customer/Dashboard';
import { ApiComponent, IApiComponentState } from '../common';
import { BackendContracts } from './Backend/Billing/BackendContracts';
import { DrupalOnboardingInstructions } from './Customer/Onboarding/DrupalOnboardingInstructions';
import { WithSite } from './WithSite';
import { WithAppContext } from '../common/contexts/AppContext';
import { SiteApplicationType } from '@edgebox/sync-core-data-definitions';
import GraphQlExplorer from './Customer/ContentCloud/Pages/GraphQlExplorer';
import ContentTypeCollectionPage from './Customer/ContentCloud/Types/ContentTypeCollectionPage';
import ContentCollectionPage from './Customer/ContentCloud/Content/ContentCollectionPage';
import AssetCollectionPage from './Customer/ContentCloud/Content/AssetCollectionPage';
import TagCollectionPage from './Customer/ContentCloud/Content/TagCollectionPage';
import { ContentEntryPage } from './Customer/ContentCloud/Content/ContentEntryPage';
import { RestExplorer } from './Customer/ContentCloud/Pages/RestExplorer';
import { ContentTypeEntryPage } from './Customer/ContentCloud/Types/ContentTypeEntryPage';
import { AssetEntryPage } from './Customer/ContentCloud/Content/AssetEntryPage';
import { TagEntryPage } from './Customer/ContentCloud/Content/TagEntryPage';
import ContentUserDataTypeCollectionPage from './Customer/ContentCloud/Types/ContentUserDataTypeCollectionPage';
import { ContentUserDataTypeEntryPage } from './Customer/ContentCloud/Types/ContentUserDataTypeEntryPage';
import { ContentTypeVisualizationPage } from './Customer/ContentCloud/Types/ContentTypeVisualizationPage';
import ClientGenerationPage from './Customer/ContentCloud/Pages/ClientGenerationPage';
import { ContentCloudOnboardingInstructions } from './Customer/Onboarding/ContentCloudOnboardingInstructions';
import { ProjectLinkDetail } from './Customer/Syndication/ProjectLinkDetail';
import { TopNavigation, isGovernancePath, isProductPath } from './TopNavigation';
import { BrandPresence } from './Customer/Governance/BrandPresence';

interface IExternalProps {}
interface IProps extends IExternalProps, ILocationProp {}

interface IState extends IApiComponentState {
  sideMenuOpen?: boolean;
}

export class RoutingContainerClass extends ApiComponent<IProps, IState> {
  async load() {
    return {};
  }

  private pageContent = React.createRef<HTMLDivElement>();

  componentDidUpdate(prevProps: Readonly<IProps>) {
    if (prevProps.location.pathname === this.props.location.pathname) {
      return;
    }
    // Following a link out of the side menu drawer closes it, so the page it opened is in view.
    if (this.state.sideMenuOpen) {
      this.setState({ sideMenuOpen: false });
    }
    // A Brand Presence screen is scrolled by the page, which stays in place while the frame moves to
    // another screen; the new screen opens at its top.
    if (isGovernancePath(prevProps.location.pathname) && isGovernancePath(this.props.location.pathname)) {
      this.pageContent.current?.scrollTo({ top: 0, left: 0 });
    }
  }

  private openSideMenu = () => {
    this.setState({ sideMenuOpen: true });
  };

  private closeSideMenu = () => {
    this.setState({ sideMenuOpen: false });
  };

  public render() {
    const pathname = this.props.location.pathname;
    const isGovernanceMode =
      !!this.props.location.pathname.match(
        /^\/projects\/[^/]+\/(settings(?:\/(overview|notifications|environments|locales|clients|links|sites|taxonomies|crawling|monitoring-prompts)(?:\/[^/]+)?)?|details|notifications|environments|locales|clients|links|sites|taxonomies|crawling|monitoring-prompts)\/?$/
      ) || isGovernancePath(this.props.location.pathname);
    const isBackendMode = !!pathname.match(/^\/backend(\/|$)/);
    const isProductMode = isProductPath(pathname);

    const showSidebar = pathname !== '/sites/register' && !isGovernanceMode && (isBackendMode || isProductMode);

    let sidebar: React.ReactNode = showSidebar ? <SidebarNavigation /> : undefined;

    const search = new URLSearchParams(this.props.location.search);

    const isContentCloudContainer = this.props.location.pathname.match(/^\/projects\/[^/]+\/content-cloud/);
    const isGovernanceContainer = isGovernancePath(this.props.location.pathname);
    const isProjectSettingsContainer = this.props.location.pathname.match(/^\/projects\/[^/]+\/settings(\/|$)/);
    const isAccountContainer = this.props.location.pathname.match(/^\/(account|subscriptions)(\/|$)/);

    // The shell's columns may be narrower and its row shorter than their content, so a page scrolls
    // inside #page-content instead of moving the whole shell sideways or the top bar away. The page
    // takes its content's height there; only Content Cloud keeps a frame-high box, because from lg up
    // its screens scroll their own panes.
    return (
      <div className={'d-flex flex-column flex-grow-1 min-width-0'}>
        <TopNavigation onOpenSideMenu={sidebar ? this.openSideMenu : undefined} />
        <div className={'d-flex align-items-stretch flex-columns flex-grow-1 min-height-0'}>
          <div className={'flex-grow-0'}>
            {sidebar ? (
              <Offcanvas
                responsive="lg"
                placement="start"
                show={!!this.state.sideMenuOpen}
                onHide={this.closeSideMenu}
                aria-label="Side menu"
              >
                <Offcanvas.Header closeButton closeLabel="Close the side menu" />
                <Offcanvas.Body className="p-0">{sidebar}</Offcanvas.Body>
              </Offcanvas>
            ) : null}
          </div>
          <div className={'flex-grow-1 min-width-0'}>
            <div
              id="page-content"
              ref={this.pageContent}
              className={`${isContentCloudContainer ? 'page-content-panes' : 'overflow-auto'} ${
                isGovernanceContainer || isProjectSettingsContainer || isAccountContainer ? 'p-0' : ''
              }`}
            >
              <div
                className={`content ${
                  this.props.location.pathname.substring(0, 8) === '/content' ||
                  this.props.location.pathname.substring(0, 8) === '/updates' ||
                  this.props.location.pathname.match(/^\/projects\/[a-z0-9]+\/(content|updates)/) ||
                  this.props.location.pathname.substring(0, 25) === '/backend/syndication/logs'
                    ? 'p-3'
                    : isGovernanceContainer || isProjectSettingsContainer || isAccountContainer
                      ? 'p-0 content-flow'
                      : 'container'
                }`}
                style={isContentCloudContainer ? { maxHeight: '100%', maxWidth: '100%', height: '100%' } : {}}
                key={'content'}
              >
                <Sentry.ErrorBoundary>
                  <Routes>
                    <Route
                      path=""
                      element={
                        <WithAppContext>
                          {(appContext) =>
                            appContext?.project?.id ? (
                              <Navigate
                                to={`/projects/${appContext.project.id}/${appContext.project.appType === SiteApplicationType.ContentCloud ? 'onboarding' : 'settings/overview'}${this.props.location.search}`}
                                replace
                              />
                            ) : (
                              <Navigate to={'/onboarding'} replace />
                            )
                          }
                        </WithAppContext>
                      }
                    />

                    <Route path="account" element={<AccountTabsLayout />}>
                      <Route path="" element={<Navigate to={'profile'} replace />} />

                      <Route path="profile" element={<AccountProfile />} />
                      <Route path="users" element={<AccountUsers />} />
                      <Route path="billing" element={<AccountBilling />} />
                      <Route path="payment" element={<AccountPayment />} />
                      <Route path="company" element={<AccountCompany />} />

                      <Route
                        path="notifications"
                        element={
                          <WithAppContext>
                            {(appContext) => (
                              <Navigate
                                to={`/projects/${appContext?.project?.id}/settings/notifications${this.props.location.search}`}
                                replace
                              />
                            )}
                          </WithAppContext>
                        }
                      />
                    </Route>

                    <Route path="sites/register" element={<RegisterSite />} />
                    <Route path="sites/register-multiple" element={<RegisterMultipleSites />} />

                    <Route path="projects" element={<ProjectList />} />
                    <Route path="projects/add" element={<ProjectAdd />} />

                    {/* Shared */}
                    <Route
                      path="projects/:project"
                      element={<Navigate to={`${this.props.location.pathname}/settings/overview${this.props.location.search}`} replace />}
                    />
                    <Route path="projects/:project/settings" element={<Navigate to={`overview${this.props.location.search}`} replace />} />
                    <Route path="projects/:project/settings/overview" element={<ProjectDetail tab="overview" />} />
                    <Route path="projects/:project/settings/notifications" element={<ProjectDetail tab="notifications" />} />
                    <Route path="projects/:project/settings/environments" element={<ProjectDetail tab="environments" />} />
                    <Route path="projects/:project/settings/locales" element={<ProjectDetail tab="locales" />} />
                    <Route path="projects/:project/settings/clients" element={<ProjectDetail tab="clients" />} />
                    <Route path="projects/:project/settings/clients/:id" element={<ProjectDetail tab="clients" />} />
                    <Route path="projects/:project/settings/clients/:id/:tab" element={<ProjectDetail tab="clients" />} />
                    <Route path="projects/:project/settings/links" element={<ProjectDetail tab="links" />} />
                    <Route path="projects/:project/settings/links/:link" element={<ProjectLinkDetail tab="details" />} />
                    <Route path="projects/:project/settings/sites" element={<ProjectDetail tab="sites" />} />
                    <Route
                      path="projects/:project/settings/sync-cores"
                      element={<Navigate to={`/sync-cores${this.props.location.search}`} replace />}
                    />

                    {/*
                      No redirect in this area addresses its target with a leading `..`. A `..` pops a route level
                      rather than a URL segment, and every route here is declared flat under the one `Routes` above,
                      so the first `..` already lands at the router root, outside `/projects/:project`, on a URL this
                      app matches with nothing. A target that sits under the route that matched may be addressed
                      with `./x`; every other target is addressed absolutely from `params.project`.
                    */}
                    <Route path="projects/:project/brand-presence" element={<BrandPresence />} />
                    <Route
                      path="projects/:project/brand-presence/onboarding"
                      element={
                        <WithParams>
                          {(params) => (
                            <Navigate
                              to={`/projects/${params.project}/brand-presence/onboarding/domain${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithParams>
                      }
                    />
                    <Route path="projects/:project/brand-presence/onboarding/:step" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/onboarding/:onboardingId/:step" element={<BrandPresence />} />
                    <Route
                      path="projects/:project/brand-presence/settings"
                      element={<Navigate to={`./taxonomies${this.props.location.search}`} replace />}
                    />
                    <Route path="projects/:project/brand-presence/settings/taxonomies" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/settings/taxonomies/:facet" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/settings/taxonomies/:facet/new" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/settings/taxonomies/:facet/:termId/edit" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/settings/taxonomies/:facet/:termId" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/settings/monitoring" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/settings/monitoring/:promptId/runs" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/settings/monitoring/:promptId/runs/:runId" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/settings/monitoring/:promptId/run" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/settings/crawling" element={<BrandPresence />} />
                    <Route
                      path="projects/:project/brand-presence/crawling-settings"
                      element={
                        <WithParams>
                          {(params) => (
                            <Navigate
                              to={`/projects/${params.project}/brand-presence/settings/crawling${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithParams>
                      }
                    />
                    <Route path="projects/:project/brand-presence/content-inventory" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/content-inventory/:key" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/content-inventory/:key/:statusOrId" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/issues" element={<BrandPresence />} />
                    <Route
                      path="projects/:project/brand-presence/analytics"
                      element={<Navigate to={`./ai-visibility${this.props.location.search}`} replace />}
                    />
                    <Route
                      path="projects/:project/brand-presence/analytics/content-health"
                      element={<Navigate to={`./page${this.props.location.search}`} replace />}
                    />
                    <Route path="projects/:project/brand-presence/analytics/content-health/:type" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/analytics/ai-visibility" element={<BrandPresence />} />
                    <Route
                      path="projects/:project/brand-presence/analytics/ai-visibility/:type"
                      element={
                        <WithParams>
                          {(params) => (
                            <Navigate
                              to={`/projects/${params.project}/brand-presence/analytics/ai-visibility${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithParams>
                      }
                    />
                    <Route path="projects/:project/brand-presence/analytics/ai-answers" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/analytics/ai-answers/:runId" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/optimize/:type" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/optimize/:type/:taskGroupId" element={<BrandPresence />} />
                    <Route
                      path="projects/:project/brand-presence/jobs"
                      element={<Navigate to={`./groups${this.props.location.search}`} replace />}
                    />
                    <Route path="projects/:project/brand-presence/jobs/groups" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/jobs/tasks" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/jobs/groups/:id" element={<BrandPresence />} />
                    <Route path="projects/:project/brand-presence/jobs/tasks/:id" element={<BrandPresence />} />
                    <Route
                      path="projects/:project/brand-presence/monitoring/:promptId/runs"
                      element={
                        <WithParams>
                          {(params) => (
                            <Navigate
                              to={`/projects/${params.project}/brand-presence/settings/monitoring/${params.promptId}/runs${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithParams>
                      }
                    />
                    <Route
                      path="projects/:project/brand-presence/monitoring/:promptId/runs/:runId"
                      element={
                        <WithParams>
                          {(params) => (
                            <Navigate
                              to={`/projects/${params.project}/brand-presence/settings/monitoring/${params.promptId}/runs/${params.runId}${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithParams>
                      }
                    />
                    <Route
                      path="projects/:project/brand-presence/monitoring/:promptId/run"
                      element={
                        <WithParams>
                          {(params) => (
                            <Navigate
                              to={`/projects/${params.project}/brand-presence/settings/monitoring/${params.promptId}/run${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithParams>
                      }
                    />
                    <Route
                      path="projects/:project/brand-presence/monitoring-prompts/:promptId/runs"
                      element={
                        <WithParams>
                          {(params) => (
                            <Navigate
                              to={`/projects/${params.project}/brand-presence/settings/monitoring/${params.promptId}/runs${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithParams>
                      }
                    />
                    <Route
                      path="projects/:project/brand-presence/monitoring-prompts/:promptId/runs/:runId"
                      element={
                        <WithParams>
                          {(params) => (
                            <Navigate
                              to={`/projects/${params.project}/brand-presence/settings/monitoring/${params.promptId}/runs/${params.runId}${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithParams>
                      }
                    />
                    <Route
                      path="projects/:project/brand-presence/monitoring-prompts/:promptId/run"
                      element={
                        <WithParams>
                          {(params) => (
                            <Navigate
                              to={`/projects/${params.project}/brand-presence/settings/monitoring/${params.promptId}/run${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithParams>
                      }
                    />

                    {/* Content Cloud specific */}

                    <Route path="projects/:project/content-cloud/content-model" element={<ContentTypeCollectionPage />} />
                    <Route path="projects/:project/content-cloud/content-model/visualize" element={<ContentTypeVisualizationPage />} />
                    <Route
                      path="projects/:project/content-cloud/content-model/:type"
                      element={<Navigate to={`${this.props.location.pathname}/fields${this.props.location.search}`} replace />}
                    />
                    <Route path="projects/:project/content-cloud/content-model/:type/:tab" element={<ContentTypeEntryPage />} />

                    <Route
                      path="projects/:project/content-cloud/content"
                      element={
                        <ContentCollectionPage byStatus={search.get('status') as null} byContentTypeId={search.get('contentTypeId')} />
                      }
                    />
                    <Route
                      path="projects/:project/content-cloud/content/:content"
                      element={<Navigate to={`${this.props.location.pathname}/content${this.props.location.search}`} replace />}
                    />
                    <Route path="projects/:project/content-cloud/content/:content/:tab" element={<ContentEntryPage />} />

                    <Route path="projects/:project/content-cloud/content-user-data" element={<ContentUserDataTypeCollectionPage />} />
                    <Route path="projects/:project/content-cloud/content-user-data/:type" element={<ContentUserDataTypeEntryPage />} />
                    <Route path="projects/:project/content-cloud/content-user-data/:type/:tab" element={<ContentUserDataTypeEntryPage />} />

                    <Route
                      path="projects/:project/content-cloud/assets"
                      element={
                        <AssetCollectionPage byStatus={search.get('status') as null} byMimeTypeGroup={search.get('mimeTypeGroup')} />
                      }
                    />
                    <Route
                      path="projects/:project/content-cloud/assets/:asset"
                      element={<Navigate to={`${this.props.location.pathname}/content${this.props.location.search}`} replace />}
                    />
                    <Route path="projects/:project/content-cloud/assets/:asset/:tab" element={<AssetEntryPage />} />

                    <Route
                      path="projects/:project/content-cloud/tags"
                      element={<TagCollectionPage byStatus={search.get('status') as null} />}
                    />
                    <Route
                      path="projects/:project/content-cloud/tags/:tags"
                      element={<Navigate to={`${this.props.location.pathname}/content${this.props.location.search}`} replace />}
                    />
                    <Route path="projects/:project/content-cloud/tags/:tags/:tab" element={<TagEntryPage />} />

                    <Route path="projects/:project/content-cloud/graphql-explorer" element={<GraphQlExplorer />} />

                    <Route
                      path="projects/:project/content-cloud/rest-explorer"
                      element={<Navigate to={`${this.props.location.pathname}/plain${this.props.location.search}`} replace />}
                    />
                    <Route path="projects/:project/content-cloud/rest-explorer/:tab" element={<RestExplorer />} />

                    <Route path="projects/:project/content-cloud/client-generation" element={<ClientGenerationPage />} />

                    {/* Sync Core specific */}

                    {/* Shared */}
                    <Route
                      path="projects/:project/onboarding"
                      element={
                        <WithAppContext>
                          {(appContext) =>
                            appContext?.project?.appType === SiteApplicationType.ContentCloud ? (
                              <ContentCloudOnboardingInstructions />
                            ) : (
                              <DrupalOnboardingInstructions />
                            )
                          }
                        </WithAppContext>
                      }
                    />
                    <Route
                      path="onboarding"
                      element={
                        <WithAppContext>
                          {(appContext) =>
                            appContext?.project ? (
                              <Navigate to={`/projects/${appContext?.project?.id}/onboarding${this.props.location.search}`} replace />
                            ) : (
                              <DrupalOnboardingInstructions />
                            )
                          }
                        </WithAppContext>
                      }
                    />
                    <Route
                      path="links"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate to={`/projects/${appContext?.project?.id}/settings/links${this.props.location.search}`} replace />
                          )}
                        </WithAppContext>
                      }
                    />

                    {/* Drupal specific */}
                    <Route
                      path="projects/:project/dashboard"
                      element={
                        <WithParams>
                          {(params) => (
                            <WithAppContext>
                              {(appContext) =>
                                appContext?.project?.id && appContext?.project?.id === params.project && appContext?.site?.id ? (
                                  <Navigate
                                    to={`/projects/${appContext.project.id}/dashboard/${appContext.site.id}${this.props.location.search}`}
                                    replace
                                  />
                                ) : (
                                  <Dashboard />
                                )
                              }
                            </WithAppContext>
                          )}
                        </WithParams>
                      }
                    />
                    <Route path="projects/:project/dashboard/:site" element={<Dashboard />} />
                    <Route
                      path="projects/:project/settings/sites/:site"
                      element={<Navigate to={`${this.props.location.pathname}/stats${this.props.location.search}`} replace />}
                    />
                    <Route
                      path="projects/:project/settings/sites/:site/:tab"
                      element={<WithParams>{(params) => <ProjectDetail tab="sites" id={params.site} />}</WithParams>}
                    />
                    <Route
                      path="projects/:project/updates"
                      element={
                        <WithParams>
                          {(params) => (
                            <WithAppContext>
                              {(appContext) =>
                                appContext?.project?.id && appContext?.project?.id === params.project && appContext?.site?.id ? (
                                  <Navigate
                                    to={`/projects/${appContext.project.id}/updates/${appContext.site.id}${this.props.location.search}`}
                                    replace
                                  />
                                ) : (
                                  <ContentDashboard activeTab="synchronizations" />
                                )
                              }
                            </WithAppContext>
                          )}
                        </WithParams>
                      }
                    />
                    <Route path="projects/:project/updates/:site" element={<ContentDashboard activeTab="synchronizations" />} />
                    <Route path="projects/:project/content/:site" element={<ContentDashboard />} />
                    <Route
                      path="projects/:project/content"
                      element={
                        <WithParams>
                          {(params) => (
                            <WithAppContext>
                              {(appContext) =>
                                appContext?.project?.id && appContext?.project?.id === params.project && appContext?.site?.id ? (
                                  <Navigate
                                    to={`/projects/${appContext.project.id}/content/${appContext.site.id}${this.props.location.search}`}
                                    replace
                                  />
                                ) : (
                                  <ContentDashboard />
                                )
                              }
                            </WithAppContext>
                          )}
                        </WithParams>
                      }
                    />

                    {/* Redirects */}
                    <Route
                      path="sites"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate to={`/projects/${appContext?.project?.id}/settings/sites${this.props.location.search}`} replace />
                          )}
                        </WithAppContext>
                      }
                    />
                    <Route
                      path="sites/:id"
                      element={
                        <WithParams>
                          {(params) => (
                            <WithSite entityId={params?.id}>
                              {(site) => (
                                <Navigate
                                  to={`/projects/${site?.project.getId()}/settings/sites/${params?.id}${this.props.location.search}`}
                                  replace
                                />
                              )}
                            </WithSite>
                          )}
                        </WithParams>
                      }
                    />

                    <Route
                      path="updates"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate to={`/projects/${appContext?.project?.id}/updates${this.props.location.search}`} replace />
                          )}
                        </WithAppContext>
                      }
                    />
                    <Route
                      path="content"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate to={`/projects/${appContext?.project?.id}/content${this.props.location.search}`} replace />
                          )}
                        </WithAppContext>
                      }
                    />

                    {/* Content Cloud specific pages */}
                    <Route
                      path="content-cloud/environments"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate
                              to={`/projects/${appContext?.project?.id}/settings/environments${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithAppContext>
                      }
                    />
                    <Route
                      path="content-cloud/locales"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate to={`/projects/${appContext?.project?.id}/settings/locales${this.props.location.search}`} replace />
                          )}
                        </WithAppContext>
                      }
                    />
                    <Route
                      path="content-cloud/clients"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate to={`/projects/${appContext?.project?.id}/settings/clients${this.props.location.search}`} replace />
                          )}
                        </WithAppContext>
                      }
                    />
                    <Route
                      path="content-cloud/clients/:clients"
                      element={
                        <WithParams>
                          {(params) => (
                            <WithAppContext>
                              {(appContext) => (
                                <Navigate
                                  to={`/projects/${appContext?.project?.id}/settings/clients/${params.clients}${this.props.location.search}`}
                                  replace
                                />
                              )}
                            </WithAppContext>
                          )}
                        </WithParams>
                      }
                    />
                    <Route
                      path="content-cloud/content-model"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate
                              to={`/projects/${appContext?.project?.id}/content-cloud/content-model${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithAppContext>
                      }
                    />
                    <Route
                      path="content-cloud/content-model/:type"
                      element={
                        <WithParams>
                          {(params) => (
                            <WithAppContext>
                              {(appContext) => (
                                <Navigate
                                  to={`/projects/${appContext?.project?.id}/content-cloud/content-model/${params.type}${this.props.location.search}`}
                                  replace
                                />
                              )}
                            </WithAppContext>
                          )}
                        </WithParams>
                      }
                    />
                    <Route
                      path="content-cloud/content"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate
                              to={`/projects/${appContext?.project?.id}/content-cloud/content${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithAppContext>
                      }
                    />
                    <Route
                      path="content-cloud/content/:content"
                      element={
                        <WithParams>
                          {(params) => (
                            <WithAppContext>
                              {(appContext) => (
                                <Navigate
                                  to={`/projects/${appContext?.project?.id}/content-cloud/content/${params.content}${this.props.location.search}`}
                                  replace
                                />
                              )}
                            </WithAppContext>
                          )}
                        </WithParams>
                      }
                    />
                    <Route
                      path="content-cloud/content-user-data"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate
                              to={`/projects/${appContext?.project?.id}/content-cloud/content-user-data${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithAppContext>
                      }
                    />
                    <Route
                      path="content-cloud/assets"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate
                              to={`/projects/${appContext?.project?.id}/content-cloud/assets${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithAppContext>
                      }
                    />
                    <Route
                      path="content-cloud/assets/:asset"
                      element={
                        <WithParams>
                          {(params) => (
                            <WithAppContext>
                              {(appContext) => (
                                <Navigate
                                  to={`/projects/${appContext?.project?.id}/content-cloud/assets/${params.asset}${this.props.location.search}`}
                                  replace
                                />
                              )}
                            </WithAppContext>
                          )}
                        </WithParams>
                      }
                    />
                    <Route
                      path="content-cloud/tags"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate to={`/projects/${appContext?.project?.id}/content-cloud/tags${this.props.location.search}`} replace />
                          )}
                        </WithAppContext>
                      }
                    />
                    <Route
                      path="content-cloud/tags/:tag"
                      element={
                        <WithParams>
                          {(params) => (
                            <WithAppContext>
                              {(appContext) => (
                                <Navigate
                                  to={`/projects/${appContext?.project?.id}/content-cloud/tags/${params.tag}${this.props.location.search}`}
                                  replace
                                />
                              )}
                            </WithAppContext>
                          )}
                        </WithParams>
                      }
                    />
                    <Route
                      path="content-cloud/rest-explorer"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate
                              to={`/projects/${appContext?.project?.id}/content-cloud/rest-explorer${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithAppContext>
                      }
                    />
                    <Route
                      path="content-cloud/client-generation"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate
                              to={`/projects/${appContext?.project?.id}/content-cloud/client-generation${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithAppContext>
                      }
                    />
                    <Route
                      path="content-cloud/graphql-explorer"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate
                              to={`/projects/${appContext?.project?.id}/content-cloud/graphql-explorer${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithAppContext>
                      }
                    />
                    <Route
                      path="content-cloud/integration"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate
                              to={`/projects/${appContext?.project?.id}/content-cloud/integration${this.props.location.search}`}
                              replace
                            />
                          )}
                        </WithAppContext>
                      }
                    />
                    <Route
                      path="content-cloud"
                      element={
                        <WithAppContext>
                          {(appContext) => (
                            <Navigate to={`/projects/${appContext?.project?.id}/onboarding${this.props.location.search}`} replace />
                          )}
                        </WithAppContext>
                      }
                    />

                    <Route path="subscriptions" element={<AccountTabsLayout />}>
                      <Route path="" element={<ContractList />} />
                    </Route>
                    <Route path="subscriptions/:id" element={<ContractDetail />} />
                    <Route path="subscriptions/:contract/revisions/:id/upgrade" element={<ContractUpgrade />} />
                    <Route path="subscriptions/:contract/revisions/:id/renew" element={<ContractUpgrade />} />

                    <Route path="sync-cores" element={<SyncCoreList />} />
                    <Route path="sync-cores/:id" element={<SyncCoreDetail />} />

                    <Route path="checkout" element={<Navigate to={'/'} replace />} />
                    <Route path="checkout/:id" element={<CheckoutPage />} />

                    <Route path="backend">
                      <Route path="" element={<Navigate to={'/backend/billing'} replace />} />

                      <Route path="settings" element={<BackendSettings />} />

                      <Route path="billing">
                        <Route path="" element={<Navigate to={'/backend/billing/customers'} replace />} />

                        <Route path="invoices/" element={<BackendInvoiceList />} />
                        <Route path="invoices/:status" element={<BackendInvoiceList />} />
                        <Route path="customers" element={<BackendCustomerList />} />
                        <Route path="customers/:id" element={<BackendCustomerDetail />} />
                        <Route path="customers/:id/contracts/add" element={<BackendAddContract />} />
                        <Route path="customers/:customerId/contracts/:id/add" element={<BackendAddContractRevision />} />
                        <Route path="contracts" element={<BackendContracts />} />
                        <Route path="contracts/:tab" element={<BackendContracts />} />
                        <Route path="contracts/:tab/:id" element={<BackendContracts />} />
                        <Route path="contracts/:id" element={<BackendContracts />} />
                      </Route>

                      <Route path="syndication">
                        <Route path="" element={<Navigate to={'/account/syndication/sites'} replace />} />

                        <Route path="projects" element={<BackendProjectList />} />
                        <Route path="sites" element={<BackendSiteList />} />
                        <Route path="sync-cores" element={<BackendSyncCoreList />} />
                        <Route path="formula" element={<BackendFormulaPlayground />} />
                        <Route path="ai" element={<Navigate to={'/backend/syndication/ai/provider'} replace />} />
                        <Route path="ai/provider" element={<BackendAiManagement tab="provider" />} />
                        <Route path="ai/model" element={<BackendAiManagement tab="model" />} />
                        <Route path="ai/prompt-template" element={<BackendAiManagement tab="prompt-template" />} />
                        <Route path="ai/prompt-execution" element={<BackendAiManagement tab="prompt-execution" />} />
                        <Route path="logs" element={<BackendLogs />} />
                      </Route>
                    </Route>

                    <Route element={<NotFoundPage />} />
                  </Routes>
                </Sentry.ErrorBoundary>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export const RoutingContainer = withLocation<IExternalProps>(RoutingContainerClass);
